// /var/www/soccer-post-operations-hub-test/src/WarehousePortal.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { getPresserSession, clearPressingSession, pressingFetch, authenticatedFetch, useAuth } from './AuthComponent';
import { useStore } from './StoreContext';
import useConsoleLocations, { WAREHOUSE_V2 } from './useConsoleLocations';
import WarehouseSearchBox from './WarehouseSearchBox';
import WarehouseProductCard from './WarehouseProductCard';
import WarehouseVariantRow from './WarehouseVariantRow';
import CameraScanner from './CameraScanner';
import { sortVariants } from './warehouseSortVariants';
import AddInventory from './AddInventory';
import CycleCount from './CycleCount';
import StockTake from './StockTake';
import RunHistory from './RunHistory';

import { Icon } from '@shopify/polaris';
import { SearchIcon, InventoryIcon, RefreshIcon, ClipboardChecklistIcon } from '@shopify/polaris-icons';
import { hasRole } from './constants/roles';
export default function WarehousePortal({ consoleMode = false }) {
    return (
        <Routes>
            <Route path="home" element={<WarehouseHome consoleMode={consoleMode} />} />
            <Route path="active" element={<WarehouseActive consoleMode={consoleMode} />} />
            {/* Add Inventory / Cycle Count / Stock Take are test-site only until Eric approves them for prod */}
            {WAREHOUSE_V2 && <Route path="add-inventory" element={<AddInventory consoleMode={consoleMode} />} />}
            {WAREHOUSE_V2 && <Route path="cycle-count" element={<CycleCount consoleMode={consoleMode} />} />}
            {WAREHOUSE_V2 && <Route path="run-history" element={<RunHistory consoleMode={consoleMode} />} />}
            {WAREHOUSE_V2 && <Route path="stock-take" element={<StockTake consoleMode={consoleMode} />} />}
            <Route path="*" element={<Navigate to="home" replace />} />
        </Routes>
    );
}

const homeTileStyle = {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14,
    padding: '28px 20px', cursor: 'pointer', gap: 10,
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)', minHeight: 110,
};
const homeGridStyle = {
    display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14, padding: '24px 16px',
};
const homeLabelStyle = { fontSize: 15, fontWeight: 700, color: '#111827', textAlign: 'center' };
const homeDescStyle  = { fontSize: 12, color: '#6b7280', textAlign: 'center' };
const homeShellStyle = { minHeight: '100vh', background: '#f3f4f6' };
const homeBarStyle   = { background: '#1e3a5f', padding: 'calc(18px + env(safe-area-inset-top, 0px)) 20px 14px', color: '#fff' };
const homeTitleStyle = { fontSize: 22, fontWeight: 800, letterSpacing: 1, margin: 0 };
const homeSubStyle   = { fontSize: 13, color: '#93c5fd', marginTop: 2 };

const INV_TOOLS = [
    { path: 'active',        label: 'Style Check',   desc: 'Set bins, counts & barcodes', icon: SearchIcon },
    { path: 'add-inventory', label: 'Add Inventory', desc: 'Log incoming stock',           icon: InventoryIcon },
    { path: 'cycle-count',   label: 'Cycle Count',   desc: 'Spot-check selected SKUs',    icon: RefreshIcon },
    { path: 'stock-take',    label: 'Stock Take',    desc: 'Full location count',          icon: ClipboardChecklistIcon },
    { path: 'run-history',   label: 'Run History',   desc: 'Past applied runs & reversal', icon: ClipboardChecklistIcon },
];

function WarehouseHome({ consoleMode }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [session] = useState(() => consoleMode ? null : getPresserSession());

    const canSeeCycleCount = consoleMode
        ? hasRole(user, ['admin', 'site_manager'])
        : (WAREHOUSE_V2 || (session?.roles || []).includes('warehouse.create_cycle_count'));   // v2: any PIN can join an open Cycle Count

    const canSeeStockTake = consoleMode
        ? hasRole(user, ['admin', 'site_manager'])
        : (WAREHOUSE_V2 || (session?.roles || []).includes('warehouse.create_stock_take'));   // v2: a PIN joins a Stock Take an admin started

    const visibleTools = INV_TOOLS.filter(t =>
        (t.path === 'active' || WAREHOUSE_V2) &&
        (t.path !== 'cycle-count' || canSeeCycleCount) &&
        (t.path !== 'stock-take'  || canSeeStockTake)
    );

    return (
        <div style={homeShellStyle}>
            <div style={homeBarStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <div style={homeTitleStyle}>WAREHOUSE</div>
                        <div style={homeSubStyle}>Choose a tool</div>
                    </div>
                    {!consoleMode && (
                        <button
                            type="button"
                            onClick={() => { clearPressingSession(); navigate('/pressing-portal/login'); }}
                            style={logoutBtnStyle}>
                            Log out
                        </button>
                    )}
                </div>
            </div>
            <div style={homeGridStyle}>
                {visibleTools.map(t => (
                    <div key={t.path} style={homeTileStyle} onClick={() => navigate(`../${t.path}`, { relative: 'path' })}>
                        <span style={{ color: '#1e3a5f' }}><Icon source={t.icon} /></span>
                        <span style={homeLabelStyle}>{t.label}</span>
                        <span style={homeDescStyle}>{t.desc}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function InvToolStub({ title, consoleMode }) {
    const navigate = useNavigate();
    return (
        <div style={homeShellStyle}>
            <div style={homeBarStyle}>
                <div style={homeTitleStyle}>{title.toUpperCase()}</div>
                <div style={homeSubStyle}>Coming soon</div>
            </div>
            <div style={{ padding: '32px 20px', textAlign: 'center', color: '#6b7280', fontSize: 15 }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🚧</div>
                <div style={{ fontWeight: 600, color: '#374151', marginBottom: 6 }}>{title}</div>
                <div>This tool is under construction.</div>
                <button
                    onClick={() => navigate(consoleMode ? '../home' : 'home', { relative: 'path' })}
                    style={{ marginTop: 24, padding: '10px 24px', background: '#1e3a5f', color: '#fff',
                             border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                    ← Back to menu
                </button>
            </div>
        </div>
    );
}

function WarehouseActive({ consoleMode = false }) {
    const navigate = useNavigate();
    // PIN kiosk session (null in consoleMode).
    const [session] = useState(() => (consoleMode ? null : getPresserSession()));
    // Signed-in console user (null in PIN mode).
    const { user } = useAuth();
    const { activeStore } = useStore();

    const [locationId, setLocationId] = useState(null);
    const [storeKey, setStoreKey]     = useState(null);
    const [showLocPicker, setShowLocPicker] = useState(false);
    const [currentProduct, setCurrentProduct] = useState(null); // {product_gid, matched_variant_gid?}

    // Product detail lives at the portal level so the header card can render
    // outside the scrolling variant list.
    const [detail, setDetail]       = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [binDraft, setBinDraft]   = useState('');
    const [busyBin, setBusyBin]     = useState(false);
    const [binFlash, setBinFlash]   = useState(false);
    const [overstockDraft, setOverstockDraft] = useState('');
    const [busyOverstock, setBusyOverstock]   = useState(false);
    const [overstockFlash, setOverstockFlash] = useState(false);
    const [rfidShelved, setRfidShelved] = useState(0);
    const [rfidShelvedByVariant, setRfidShelvedByVariant] = useState({});
    const binFlashTimer       = useRef(null);
    const overstockFlashTimer = useRef(null);
    useEffect(() => () => {
        clearTimeout(binFlashTimer.current);
        clearTimeout(overstockFlashTimer.current);
    }, []);
    const [busyRS,  setBusyRS]      = useState(false);
    const reloadIdRef = useRef(0);
    const lastScrolledRef = useRef(null);

    // Console-mode locations: scoped to the user and to the location picked in the sidebar nav.
    const consoleLocs = useConsoleLocations(consoleMode, { includeStores: true });

    // Build the assignment list. PIN mode uses session.assignments; console mode the scoped list above.
    const myAssignments = useMemo(() => {
        if (!consoleMode) return session?.assignments || [];
        if (!Array.isArray(consoleLocs)) return [];
        return consoleLocs.map(l => ({
            shopify_location_id: l.shopify_location_id,
            location_name:       l.name,
            store_key:           activeStore,
        }));
    }, [consoleMode, session, consoleLocs, activeStore]);

    useEffect(() => {
        if (consoleMode) {
            if (!user) {
                navigate('/login', { replace: true });
                return;
            }
        } else {
            if (!session) {
                navigate('/pressing-portal/login', { replace: true });
                return;
            }
            if (!session.roles?.includes('warehouse')) {
                navigate('/pressing-portal/login', { replace: true });
                return;
            }
        }
        // In console mode, wait for the /api/locations fetch to settle before
        // judging "no assignments" — consoleLocs starts as null while loading.
        if (consoleMode && consoleLocs === null) return;
        if (myAssignments.length === 0) {
            showToastRef.current?.('err', consoleMode
                ? 'No locations available for your account in this store.'
                : 'No locations assigned to this PIN.');
            setLocationId(null);
            setStoreKey(null);
        } else if (myAssignments.length === 1) {
            setLocationId(myAssignments[0].shopify_location_id);
            setStoreKey(myAssignments[0].store_key);
        } else {
            const stillValid = locationId && myAssignments.some(
                a => String(a.shopify_location_id) === String(locationId)
            );
            if (!stillValid) {
                setLocationId(null);
                setStoreKey(null);
                setShowLocPicker(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [consoleMode, session, user, navigate, myAssignments]);

    // Single fetch helper passed to every child. Encapsulates the two modes'
    // auth token, fetcher, and presser_id query-string suffix.
    const apiFetch = useMemo(() => {
        const appendQS = (path, extra) => {
            if (!extra) return path;
            return `${path}${path.includes('?') ? '&' : '?'}${extra}`;
        };
        if (consoleMode) {
            return (path, opts = {}) => {
                const url = appendQS(path, `store=${encodeURIComponent(storeKey || '')}`);
                return authenticatedFetch(url, opts);
            };
        }
        const presserId = session?.presser_id;
        return (path, opts = {}) => {
            const url = appendQS(path,
                `store=${encodeURIComponent(storeKey || '')}&presser_id=${encodeURIComponent(presserId)}`);
            return pressingFetch(url, opts);
        };
    }, [consoleMode, storeKey, session]);

    // Load product detail when product or location changes.
    const reloadDetail = useCallback(async () => {
        if (!currentProduct?.product_gid || !locationId) return;
        const reloadId = ++reloadIdRef.current;
        setDetailLoading(true);
        try {
            const url = `/api/portal/warehouse/product/${encodeURIComponent(currentProduct.product_gid)}`
                + `?shopify_location_id=${encodeURIComponent(locationId)}`;
            const r = await apiFetch(url, { method: 'GET' });
            if (reloadId !== reloadIdRef.current) return;
            if (r.ok) {
                const body = await r.json();
                if (reloadId !== reloadIdRef.current) return;
                setDetail(body);
                setBinDraft(body.product_bin || '');
                setOverstockDraft(body.product_overstock_bin || '');
                if (user?.rfid_enabled) {
                    apiFetch(`/api/rfid/boxes?status=shelved&product_gid=${encodeURIComponent(currentProduct.product_gid)}&shopify_location_id=${encodeURIComponent(locationId)}`, { method: 'GET' })
                        .then(rr => (rr.ok ? rr.json() : { boxes: [] }))
                        .then(j => {
                            if (reloadId !== reloadIdRef.current) return;
                            const boxes = j.boxes || [];
                            setRfidShelved(boxes.length);
                            const byVariant = {};
                            for (const box of boxes) {
                                if (!box.variant_gid) continue;
                                byVariant[box.variant_gid] = (byVariant[box.variant_gid] || 0) + 1;
                            }
                            setRfidShelvedByVariant(byVariant);
                        })
                        .catch(() => {
                            if (reloadId === reloadIdRef.current) {
                                setRfidShelved(0);
                                setRfidShelvedByVariant({});
                            }
                        });
                } else {
                    setRfidShelved(0);
                    setRfidShelvedByVariant({});
                }
            } else {
                showToastRef.current?.('err', `Load failed (${r.status})`);
            }
        } finally {
            if (reloadId === reloadIdRef.current) setDetailLoading(false);
        }
    }, [currentProduct, locationId, apiFetch, user]);

    useEffect(() => { reloadDetail(); }, [reloadDetail]);

    const patchVariant = useCallback((variantGid, patch) => {
        setDetail(d => d ? {
            ...d,
            variants: (d.variants || []).map(x => x.variant_gid === variantGid ? { ...x, ...patch } : x),
        } : d);
    }, []);

    const postMetafield = useCallback(async (body) => {
        return apiFetch(`/api/portal/warehouse/metafield/set`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify(body),
        });
    }, [apiFetch]);

    const saveProductBin = useCallback(async () => {
        if (!detail) return;
        const next = (binDraft || '').trim().toUpperCase();
        const prev = (detail.product_bin || '').trim();
        if (busyBin || next === prev) return;
        setBusyBin(true);
        try {
            const r = await postMetafield({
                field: 'bin',
                label: detail.title || null,
                owner_gid: detail.owner_gid || currentProduct?.product_gid,
                shopify_location_id: locationId,
                value_before: prev || null,
                value_after:  next || null,
            });
            if (r.ok) {
                setDetail(d => ({
                    ...d,
                    product_bin: next,
                    variants: (d?.variants || []).map(v => ({ ...v, bin: next })),
                }));
                showToastRef.current?.('ok', 'Bin saved');
                setBinFlash(true);
                clearTimeout(binFlashTimer.current);
                binFlashTimer.current = setTimeout(() => setBinFlash(false), 1200);
            } else {
                const err = await r.json().catch(() => ({}));
                showToastRef.current?.('err', err.detail || 'Bin save failed');
                setBinDraft(prev || '');
            }
        } finally { setBusyBin(false); }
    }, [postMetafield, currentProduct, locationId, detail, binDraft, busyBin]);

    const saveProductOverstock = useCallback(async () => {
        if (!detail) return;
        const next = (overstockDraft || '').trim().toUpperCase();
        const prev = (detail.product_overstock_bin || '').trim();
        if (busyOverstock || next === prev) return;
        setBusyOverstock(true);
        try {
            const r = await postMetafield({
                field: 'overstock',
                label: detail.title || null,
                owner_gid: detail.owner_gid || currentProduct?.product_gid,
                shopify_location_id: locationId,
                value_before: prev || null,
                value_after:  next || null,
            });
            if (r.ok) {
                setDetail(d => ({
                    ...d,
                    product_overstock_bin: next,
                    variants: (d?.variants || []).map(v => ({ ...v, overstock_bin: next })),
                }));
                showToastRef.current?.('ok', 'Overstock saved');
                setOverstockFlash(true);
                clearTimeout(overstockFlashTimer.current);
                overstockFlashTimer.current = setTimeout(() => setOverstockFlash(false), 1200);
            } else {
                const err = await r.json().catch(() => ({}));
                showToastRef.current?.('err', err.detail || 'Overstock save failed');
                setOverstockDraft(prev || '');
            }
        } finally { setBusyOverstock(false); }
    }, [postMetafield, currentProduct, locationId, detail, overstockDraft, busyOverstock]);

    const toggleRequireScanning = useCallback(async () => {
        if (!detail || busyRS) return;
        const prev = !!detail.require_scanning;
        const next = !prev;
        setBusyRS(true);
        try {
            const r = await postMetafield({
                field: 'require_scanning',
                label: detail.title || null,
                owner_gid: detail.owner_gid || currentProduct?.product_gid,
                shopify_location_id: locationId,
                value_before: prev,
                value_after:  next,
            });
            if (r.ok) {
                setDetail(d => ({ ...d, require_scanning: next }));
                showToastRef.current?.('ok', next ? 'Require scanning ON' : 'Require scanning OFF');
            } else {
                const err = await r.json().catch(() => ({}));
                showToastRef.current?.('err', err.detail || 'Save failed');
            }
        } finally { setBusyRS(false); }
    }, [postMetafield, currentProduct, locationId, detail, busyRS]);

    // Reset detail on EVERY product pick (even re-scan of the same product)
    // and on location change. `currentProduct` is a fresh object on every pick
    // from search/scan, so the ref change here fires this effect each time —
    // letting the user see a clean reload, not a stale-detail flash. We also
    // null `lastScrolledRef` so the scroll-to-matched-variant fires again.
    useEffect(() => {
        setDetail(null);
        setRfidShelved(0);
        setRfidShelvedByVariant({});
        lastScrolledRef.current = null;
    }, [currentProduct, locationId]);

    // --- Toast (dark pill) ---
    const [toast, setToast] = useState(null);
    const toastTimer = useRef(null);
    const showToastRef = useRef(null);
    const showToast = useCallback((kind, message) => {
        // FastAPI 422 returns `detail` as an array of objects; rendering that as
        // a React child crashes the page. Always coerce to a string.
        let msg;
        if (message == null) msg = '';
        else if (typeof message === 'string') msg = message;
        else if (Array.isArray(message)) {
            msg = message.map(m => (m && typeof m === 'object' && m.msg) ? m.msg : JSON.stringify(m)).join('; ');
        } else if (typeof message === 'object') {
            msg = message.msg || JSON.stringify(message);
        } else msg = String(message);
        setToast({ kind, message: msg });
        clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(null), 2200);
    }, []);
    showToastRef.current = showToast;
    useEffect(() => () => clearTimeout(toastTimer.current), []);

    // --- Scan flash ---
    const [scanFlash, setScanFlash] = useState(false);
    const scanFlashTimer = useRef(null);
    const triggerScanFlash = useCallback(() => {
        setScanFlash(true);
        clearTimeout(scanFlashTimer.current);
        scanFlashTimer.current = setTimeout(() => setScanFlash(false), 500);
    }, []);
    useEffect(() => () => clearTimeout(scanFlashTimer.current), []);

    // --- Camera scanner ---
    const [cameraOpen, setCameraOpen] = useState(false);
    const onCameraDetect = useCallback(async (barcode) => {
        setCameraOpen(false);
        const term = (barcode || '').trim();
        if (!term || !locationId) return;
        try {
            const qs = `q=${encodeURIComponent(term)}&shopify_location_id=${encodeURIComponent(locationId)}&exact=1`;
            const r = await apiFetch(`/api/portal/warehouse/search?${qs}`, { method: 'GET' });
            if (!r.ok) { showToastRef.current?.('err', `Scan failed (${r.status})`); return; }
            const data = await r.json();
            const items = data.results || [];
            if (items.length === 0) {
                showToastRef.current?.('err', `No match for ${term}`);
                return;
            }
            const hit = items[0];
            setCurrentProduct({ product_gid: hit.product_gid, matched_variant_gid: hit.matched_variant_gid });
            triggerScanFlash();
        } catch (e) {
            showToastRef.current?.('err', 'Scan lookup failed');
        }
    }, [apiFetch, locationId, triggerScanFlash]);

    // --- Scroll-to-and-flash matched variant after detail loads ---
    const variantRefs = useRef({});
    const productGid = currentProduct?.product_gid;
    const matchedGid = currentProduct?.matched_variant_gid;
    useEffect(() => {
        if (!detail || !matchedGid) return;
        const sentinel = `${productGid}|${matchedGid}`;
        if (lastScrolledRef.current === sentinel) return;
        const node = variantRefs.current[matchedGid];
        if (node) {
            lastScrolledRef.current = sentinel;
            requestAnimationFrame(() => {
                try { node.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) {}
            });
        }
    }, [detail, productGid, matchedGid]);

    if (consoleMode && !user) return null;
    if (!consoleMode && !session) return null;

    const sortedVariants = detail ? sortVariants(detail.variants || []) : [];
    const activeAssignment = myAssignments.find(a => String(a.shopify_location_id) === String(locationId));
    const showMatchHighlight = matchedGid && sortedVariants.length > 1;

    return (
        <div style={shellStyle}>
            {/* Header — same navy bar as Add Inventory / Cycle Count / Stock Take */}
            <div style={barStyle}>
                <div style={barRowStyle}>
                    <div>
                        <div style={barTitleStyle}>STYLE CHECK</div>
                        <div style={barSubStyle}>{activeAssignment?.location_name || 'Select location'}</div>
                    </div>
                    {locationId && myAssignments.length > 1 && (
                        <button type="button" style={barBtnStyle} onClick={() => setShowLocPicker(true)}>📍 Change</button>
                    )}
                </div>
                <button type="button" style={barBtnStyle} onClick={() => navigate('../home', { relative: 'path' })}>
                    ← Back to menu
                </button>
            </div>

            {/* Scan card — same white card as the other tools; stays in view while the variant list scrolls */}
            <div style={scanCardStyle}>
                <div style={scanLabelStyle}>SCAN OR SEARCH ITEM</div>
                {locationId && storeKey && (
                    <WarehouseSearchBox
                        storeKey={storeKey}
                        shopifyLocationId={locationId}
                        apiFetch={apiFetch}
                        onProductPicked={setCurrentProduct}
                        onError={(m) => showToast('err', m)}
                        onScanFlash={triggerScanFlash}
                        onScanClick={() => setCameraOpen(true)}
                    />
                )}
            </div>

            {/* Fixed product header (only when detail loaded) — flex:none, NO position:sticky */}
            {detail && (
                <div style={headerHolderStyle}>
                    <WarehouseProductCard
                        detail={detail}
                        binDraft={binDraft}
                        setBinDraft={setBinDraft}
                        busyBin={busyBin}
                        binFlash={binFlash}
                        busyRS={busyRS}
                        onSaveBin={saveProductBin}
                        onToggleRS={toggleRequireScanning}
                        overstockDraft={overstockDraft}
                        setOverstockDraft={setOverstockDraft}
                        busyOverstock={busyOverstock}
                        overstockFlash={overstockFlash}
                        onSaveOverstock={saveProductOverstock}
                        rfidShelvedBoxes={rfidShelved}
                    />
                </div>
            )}

            {/* Scrolling variant list (or empty state) */}
            <div style={scrollAreaStyle}>
                {!detail && detailLoading && (
                    <div style={loadingStateStyle}>Loading product…</div>
                )}
                {!detail && !detailLoading && (
                    <div style={emptyStateStyle}>
                        <div style={emptyTileStyle}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
                                 stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="7"/>
                                <path d="M21 21l-4.3-4.3"/>
                            </svg>
                        </div>
                        <div style={{ fontSize: 17, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
                            Scan a product to begin
                        </div>
                        <div style={{ fontSize: 13.5, color: '#9ca3af', maxWidth: 240, textAlign: 'center' }}>
                            Scan a barcode or type a SKU. Edits save automatically.
                        </div>
                    </div>
                )}
                {detail && (
                    <>
                        <div style={listHeaderStyle}>
                            <span style={listHeaderLabelStyle}>Sizes &amp; Inventory</span>
                            <span style={listHeaderHintStyle}>✓ Auto-saves</span>
                        </div>
                        {sortedVariants.map(v => {
                            const isMatched = showMatchHighlight && v.variant_gid === matchedGid;
                            return (
                                <div
                                    key={v.variant_gid}
                                    ref={(node) => {
                                        if (node) variantRefs.current[v.variant_gid] = node;
                                        else delete variantRefs.current[v.variant_gid];
                                    }}
                                    className={isMatched ? 'warehouse-row--matched' : undefined}>
                                    <WarehouseVariantRow
                                        storeKey={storeKey}
                                        shopifyLocationId={locationId}
                                        variant={v}
                                        showVariantBin={detail.bin_level === 'variant'}
                                        showVariantOverstock={detail.overstock_bin_level === 'variant'}
                                        rfidShelvedBoxes={rfidShelvedByVariant[v.variant_gid] || 0}
                                        apiFetch={apiFetch}
                                        onPatchVariant={(patch) => patchVariant(v.variant_gid, patch)}
                                        onError={(m) => showToast('err', m)}
                                        onSuccess={(m) => showToast('ok', m)}
                                    />
                                </div>
                            );
                        })}
                    </>
                )}
            </div>

            {/* Location picker — same overlay + buttons as Stock Take / Cycle Count / Add Inventory */}
            {showLocPicker && (
                <div style={pickerOverlayStyle}
                     onClick={() => { if (locationId) setShowLocPicker(false); }}>
                    <div style={pickerSheetStyle} onClick={(e) => e.stopPropagation()}>
                        <div style={pickerTitleStyle}>Select your location</div>
                        {myAssignments.length === 0 && (
                            <div style={{ fontSize: 13, color: '#6d7175', padding: '12px 4px' }}>
                                No locations available.
                            </div>
                        )}
                        {myAssignments.map(a => (
                            <button
                                key={a.shopify_location_id}
                                type="button"
                                style={pickerBtnStyle}
                                onClick={() => {
                                    setStoreKey(a.store_key);
                                    setLocationId(a.shopify_location_id);
                                    setCurrentProduct(null);  // reset product detail on location change
                                    setShowLocPicker(false);
                                }}>
                                {a.location_name || a.shopify_location_id}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Toast (dark pill) */}
            {toast && (
                <div className="warehouse-toast" role="status" aria-live="polite">
                    <span className={`warehouse-toast__dot warehouse-toast__dot--${toast.kind === 'ok' ? 'ok' : 'err'}`} />
                    <span>{toast.message}</span>
                </div>
            )}

            {/* Scan flash (transient full-screen wash) */}
            {scanFlash && <div className="warehouse-scan-flash" />}

            {/* Camera scanner overlay */}
            {cameraOpen && (
                <CameraScanner
                    onDetect={onCameraDetect}
                    onClose={() => setCameraOpen(false)}
                />
            )}
        </div>
    );
}

// --- Inline style constants ---

// Layout shared with Add Inventory / Cycle Count / Stock Take
const shellStyle = { minHeight: '100vh', background: '#f3f4f6', display: 'flex', flexDirection: 'column', position: 'relative' };
const barStyle = { background: '#1e3a5f', padding: 'calc(16px + env(safe-area-inset-top, 0px)) 16px 12px', color: '#fff', flexShrink: 0 };
const barRowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
const barTitleStyle = { fontSize: 20, fontWeight: 800, letterSpacing: 1 };
const barSubStyle = { fontSize: 12, color: '#93c5fd', marginTop: 2 };
const barBtnStyle = { background: 'none', border: 'none', color: '#93c5fd', fontSize: 13, cursor: 'pointer', padding: '4px 0', marginTop: 4, fontFamily: 'inherit' };
const scanCardStyle = {
    background: '#fff', margin: '12px 12px 0', borderRadius: 10, padding: '12px 14px',
    boxShadow: '0 1px 4px rgba(0,0,0,.07)', position: 'sticky', top: 'env(safe-area-inset-top, 0px)', zIndex: 50,
};
const scanLabelStyle = { fontSize: 12, color: '#6b7280', marginBottom: 6, fontWeight: 600 };
const logoutBtnStyle = {
    background: '#f4f6f8', border: '1px solid #e4e7eb',
    borderRadius: 10, padding: '7px 11px', color: '#52606d', cursor: 'pointer',
    fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
};
const headerHolderStyle = {
    flex: 'none', background: '#f3f4f6', padding: '12px 12px 8px',
};
const scrollAreaStyle = {
    flex: 1, minHeight: 0, overflowY: 'auto', background: '#f3f4f6',
    padding: '8px 12px 60px',
};
const loadingStateStyle = {
    padding: '40px 16px', textAlign: 'center', color: '#6d7175', fontSize: 13.5,
};
const emptyStateStyle = {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    textAlign: 'center', padding: '80px 24px', color: '#9ca3af',
};
const emptyTileStyle = {
    width: 72, height: 72, borderRadius: 18, background: '#e7e9ee',
    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
};
const listHeaderStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 4px 8px',
};
const listHeaderLabelStyle = {
    fontSize: 11, fontWeight: 700, letterSpacing: '0.07em',
    textTransform: 'uppercase', color: '#9ca3af',
};
const listHeaderHintStyle = { fontSize: 11, color: '#9ca3af' };
// Location picker — identical to the one in Stock Take / Cycle Count / Add Inventory.
const pickerOverlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 600,
    display: 'flex', alignItems: 'flex-end',
};
const pickerSheetStyle = {
    background: '#fff', borderRadius: '16px 16px 0 0', padding: '20px 16px 32px',
    width: '100%', maxHeight: '60vh', overflowY: 'auto',
};
const pickerTitleStyle = { fontSize: 17, fontWeight: 700, marginBottom: 14, color: '#111827' };
const pickerBtnStyle = {
    width: '100%', textAlign: 'left', padding: '12px 14px', margin: '4px 0',
    border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff',
    fontSize: 14, cursor: 'pointer', fontWeight: 600, color: '#1e3a5f',
};
